import { useEffect, useState, type RefObject } from 'react';
import type { FormRef } from '../types';

/**
 * Custom hook for watching form field values with a cleaner API than formRef.current?.watch()
 *
 * @param formRef - Reference to the Form component
 * @param fieldName - Optional field name to watch. If omitted, watches all fields.
 * @returns The current value of the watched field(s)
 *
 * @example
 * ```tsx
 * const formRef = useRef<FormRef>(null);
 *
 * // Watch a single field
 * const paymentMethod = useFormWatch(formRef, 'preferredPaymentMethod');
 *
 * // Watch all fields
 * const allValues = useFormWatch(formRef);
 *
 * useEffect(() => {
 *   if (paymentMethod === 'Cash') {
 *     // Do something when payment method is Cash
 *   }
 * }, [paymentMethod]);
 * ```
 */
export const useWatch = <T = unknown>(
  formRef: RefObject<FormRef | null>,
  fieldName?: string
): T | undefined => {
  const [value, setValue] = useState<T | undefined>(() => {
    // Initialize with current value if formRef is available
    if (formRef.current) {
      return formRef.current.watch<T>(fieldName);
    }
    return undefined;
  });

  useEffect(() => {
    if (!formRef.current) return;

    // Get initial value
    setValue(formRef.current.watch<T>(fieldName));

    // Subscribe to changes
    const subscription = formRef.current.subscribe((values, info) => {
      // If watching a specific field, only update when that field changes
      if (fieldName) {
        if (info.name === fieldName || !info.name) {
          setValue(values[fieldName] as T);
        }
      } else {
        // Watching all fields
        setValue(values as T);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [formRef, fieldName]);

  return value;
};

export default useWatch;
